import { useEffect, useMemo, useRef, useState } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { TRY, NUM, pname } from "@/lib/format";
import { getProductIndex, refreshProductIndex, searchProducts } from "@/lib/productIndex";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PackageSearch, Loader2, Search } from "lucide-react";
import { StockSplit } from "@/components/StockSplit";
import { toast } from "sonner";

// Stoktan ürün seçici — ürün dizini bir kez indirilir, arama tarayıcıda ANLIK yapılır; seçilince onSelect(product)
export default function ProductPicker({ onSelect, label = "Stoktan Ürün", size = "sm", variant = "outline", className = "", testid = "product-picker", defaultOpen = false, onClosed }) {
  const [open, setOpen] = useState(defaultOpen);
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState({ brands: [], categories: [], series: [], series_by_brand: {}, categories_by_brand: {} });
  const [filters, setFilters] = useState({ brand: "", series: "", category: "" });
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/products/facets").then((r) => setFacets(r.data || {})).catch(() => {});
    let alive = true;
    setLoading(!index);
    getProductIndex().then((list) => { if (alive) { setIndex(list); refreshProductIndex((fresh) => alive && setIndex(fresh)); } }).catch((e) => toast.error(apiError(e))).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]); // eslint-disable-line

  const hasFilter = !!(filters.brand || filters.series || filters.category);
  const results = useMemo(() => {
    if (!index || (!q.trim() && !hasFilter)) return [];
    return searchProducts(index, q, filters, 300);
  }, [index, q, filters, hasFilter]);

  const pick = (p) => {
    onSelect(p);
    setOpen(false); setQ(""); onClosed?.();
    toast.success(`${pname(p)} eklendi`);
  };
  const onKey = (e) => { if (e.key === "Enter" && results.length === 1) { e.preventDefault(); pick(results[0]); } };
  const seriesOpts = filters.brand ? (facets.series_by_brand?.[filters.brand] || []) : (facets.series || []);
  const catOpts = filters.brand ? (facets.categories_by_brand?.[filters.brand] || []) : (facets.categories || []);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setQ(""); onClosed?.(); } }}>
      <PopoverTrigger asChild>
        <Button type="button" size={size} variant={variant} className={className} data-testid={`${testid}-trigger`}>
          <PackageSearch className={label ? "h-3.5 w-3.5 mr-1.5" : "h-4 w-4"} />{label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(640px,calc(100vw-16px))] p-0" align="start" sideOffset={6} collisionPadding={12} data-testid={`${testid}-content`}
        onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); setTimeout(() => inputRef.current?.focus(), 30); }}
        onFocusOutside={(e) => { if (defaultOpen) e.preventDefault(); }}
        onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
        <div className="relative border-b">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input ref={inputRef} placeholder="Ürün adı, kodu veya barkodu..." value={q}
            onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} className="pl-9 border-0 focus-visible:ring-0 h-11"
            data-testid={`${testid}-search`} />
          {index && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground tabular-nums" data-testid={`${testid}-count`}>{(q.trim() || hasFilter) ? `${results.length}${results.length >= 300 ? "+" : ""} sonuç` : `${index.length} ürün`}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 p-2 border-b bg-muted/30">
          <Select value={filters.brand || "__all"} onValueChange={(v) => setFilters({ brand: v === "__all" ? "" : v, series: "", category: "" })}>
            <SelectTrigger className="h-8 w-[118px] text-xs" data-testid={`${testid}-brand`}><SelectValue placeholder="Marka" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tüm Markalar</SelectItem>
              {(facets.brands || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.series || "__all"} onValueChange={(v) => setFilters({ ...filters, series: v === "__all" ? "" : v })}>
            <SelectTrigger className="h-8 w-[136px] text-xs" data-testid={`${testid}-series`}><SelectValue placeholder="Seri" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tüm Seriler</SelectItem>
              {seriesOpts.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category || "__all"} onValueChange={(v) => setFilters({ ...filters, category: v === "__all" ? "" : v })}>
            <SelectTrigger className="h-8 w-[126px] text-xs" data-testid={`${testid}-category`}><SelectValue placeholder="Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tüm Kategoriler</SelectItem>
              {catOpts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasFilter ? <button type="button" onClick={() => setFilters({ brand: "", series: "", category: "" })} className="text-xs text-muted-foreground underline" data-testid={`${testid}-clear`}>Temizle</button> : null}
        </div>
        <div className="max-h-[min(60vh,460px)] min-h-[140px] overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }} onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} data-testid={`${testid}-list`}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2 text-xs"><Loader2 className="h-5 w-5 animate-spin" />Ürün dizini yükleniyor (bir kez)…</div>
          ) : (!q.trim() && !hasFilter) ? (
            <div className="py-8 text-center text-sm text-muted-foreground"><PackageSearch className="h-6 w-6 mx-auto mb-2 opacity-40" />Aramak için yazın veya marka/seri/kategori seçin</div>
          ) : results.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Ürün bulunamadı</div>
          ) : (
            <>
            {results.length >= 300 && <div className="px-3 py-1.5 text-[11px] text-amber-800 bg-amber-50 border-b" data-testid={`${testid}-more`}>İlk 300 ürün gösteriliyor — daraltmak için seri/kategori seçin veya arayın.</div>}
            {results.map((p) => (
              <button type="button" key={p.id} onClick={() => pick(p)}
                className="w-full text-left px-3 py-2.5 hover:bg-muted/60 border-b last:border-0 transition-colors"
                title={pname(p)} data-testid={`${testid}-item-${p.id}`}>
                <div className="flex items-center gap-2.5">
                  {(p.image_url || p.images?.[0]) ? <img src={fileUrl(p.image_url || p.images[0])} alt="" className="h-9 w-9 rounded object-contain bg-white border shrink-0" /> : null}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-semibold text-sm leading-snug whitespace-normal break-words" data-testid={`${testid}-item-name-${p.id}`}>{pname(p)}</span>
                      <span className="text-sm font-bold text-primary tabular-nums shrink-0 leading-snug">{TRY(p.price)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                      {p.brand && <span className="bg-secondary/10 text-secondary px-1.5 rounded">{p.brand}</span>}
                      {(p.series || (p.discount_group && p.discount_group !== p.brand)) && <span className="truncate">{p.series || p.discount_group}</span>}
                      {p.code && <span className="font-mono">{p.code}</span>}
                      {p.currency && p.currency !== "TL" && <span>{NUM(p.list_orig ?? p.list_price)} {p.currency}</span>}
                      <StockSplit p={p} total className="ml-auto" testid={`pp-stock-${p.id}`} />
                    </div>
                  </div>
                </div>
              </button>
            ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
